import type {
  ClickPayload,
  ConversionOrder,
  ConversionPayload,
  Identity,
  ImpressionItem,
  ImpressionPayload
} from './types';

export function buildImpressionPayload(
  queryId: string,
  identity: Identity,
  items: ImpressionItem[]
): ImpressionPayload {
  const firstPage = items.find((item) => item.page !== undefined)?.page;
  const payload: ImpressionPayload = {
    v: 1,
    query_id: queryId,
    session_id: identity.session_id,
    anonymous_user_id: identity.anonymous_user_id,
    items: items.map((item) =>
      item.page === undefined ? { sku: item.sku, position: item.position } : { ...item }
    )
  };
  if (identity.user_id_hash !== undefined) payload.user_id_hash = identity.user_id_hash;
  if (firstPage !== undefined) payload.page = firstPage;
  return payload;
}

export function buildClickPayload(
  queryId: string,
  identity: Identity,
  sku: string,
  position: number,
  page?: number
): ClickPayload {
  const payload: ClickPayload = {
    v: 1,
    query_id: queryId,
    session_id: identity.session_id,
    anonymous_user_id: identity.anonymous_user_id,
    sku,
    position
  };
  if (identity.user_id_hash !== undefined) payload.user_id_hash = identity.user_id_hash;
  if (page !== undefined) payload.page = page;
  return payload;
}

export function buildConversionPayload(identity: Identity, order: ConversionOrder): ConversionPayload {
  return {
    v: 1,
    session_id: identity.session_id,
    anonymous_user_id: identity.anonymous_user_id,
    order_id: order.order_id,
    currency: order.currency,
    items: order.items.map((item) => ({
      sku: item.sku,
      quantity: item.quantity,
      unit_price: item.unit_price
    }))
  };
}

export function validSku(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export function validPosition(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

export function validPage(value: unknown): value is number | undefined {
  return value === undefined || (typeof value === 'number' && Number.isInteger(value) && value > 0);
}

export function validQueryId(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export function validConversion(order: unknown): ConversionOrder | undefined {
  if (!isRecord(order) || !isNonEmpty(order.order_id)) return undefined;
  const currency = typeof order.currency === 'string' ? order.currency.trim().toUpperCase() : '';
  if (!/^[A-Z]{3}$/.test(currency)) return undefined;
  if (!Array.isArray(order.items) || order.items.length === 0 || order.items.length > 50) return undefined;
  if (
    !order.items.every(isConversionItem)
  )
    return undefined;
  return {
    order_id: order.order_id.trim(),
    currency,
    items: order.items.map((item) => ({ sku: item.sku.trim(), quantity: item.quantity, unit_price: item.unit_price }))
  };
}

export function devWarn(message: string): void {
  if (isDevBuild() && typeof console !== 'undefined') {
    console.warn(`[vidisape] ${message}`);
  }
}

function isNonEmpty(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

type ConversionItem = ConversionOrder['items'][number];

function isConversionItem(value: unknown): value is ConversionItem {
  if (!isRecord(value)) return false;
  return (
    isNonEmpty(value.sku) &&
    typeof value.quantity === 'number' &&
    Number.isInteger(value.quantity) &&
    value.quantity >= 1 &&
    typeof value.unit_price === 'number' &&
    Number.isFinite(value.unit_price) &&
    value.unit_price >= 0
  );
}

function isDevBuild(): boolean {
  try {
    return typeof process !== 'undefined' && process.env.NODE_ENV !== 'production';
  } catch {
    return false;
  }
}
